import { useEffect, useMemo, useState } from "react";
import { db, listExports } from "../../db";
import { classifyCells, orderDecisionLog, resolveDecisions, type RecoveryDecision } from "../../engine/decisions";
import type { RecoveryReview } from "../../engine/review";
import { useRunProfile } from "../profiles/use-run-profile";
import { countDecisionProgress, type DecisionProgress } from "./progress";

/**
 * Decision progress and export receipts for the run on screen.
 *
 * Both reads are scoped by the run's `analysisKey` — decisions since schema v9,
 * export receipts all along. Until then decisions were stored under
 * `review.generatedAt`, and the two strings were interchangeable to the type
 * system but not to the data; that trap is gone with the rekey.
 *
 * Both reads degrade to "nothing recorded". The guided flow then understates
 * progress, which is the safe direction: a step that fails to tick invites
 * someone to look, while one that ticks wrongly tells them to stop looking.
 */
/** Stable identity, so deriving an empty log does not re-run the memo below. */
const EMPTY_LOG: RecoveryDecision[] = [];

export function useRunProgress(review: RecoveryReview | null, analysisKey: string | null): {
  decisions: DecisionProgress | null;
  exported: boolean;
} {
  const { profile } = useRunProfile(review?.profileId ?? null);
  // Each read is stored WITH the run it belongs to, and the values below are
  // derived from that. Clearing them in an effect when the run changes would
  // cost a render — and, until that render, this hook would report the previous
  // run's progress for the new run, which is the one thing it must not do.
  const [loaded, setLoaded] = useState<{ scope: string; rows: RecoveryDecision[] }>({ scope: "", rows: [] });
  const [exportedRead, setExportedRead] = useState<{ key: string; exported: boolean }>({ key: "", exported: false });
  // The run's analysis key, which is what the decision log is scoped by since
  // schema v9 — not the review timestamp it used to be keyed on.
  const decisionScope = analysisKey ?? "";

  const log = decisionScope !== "" && loaded.scope === decisionScope ? loaded.rows : EMPTY_LOG;
  const exported = analysisKey !== null && exportedRead.key === analysisKey && exportedRead.exported;

  useEffect(() => {
    if (review === null || decisionScope === "") return;
    let cancelled = false;
    void db.decisions
      .where("analysisKey")
      .equals(decisionScope)
      .toArray()
      .then((rows) => {
        if (!cancelled) setLoaded({ scope: decisionScope, rows: orderDecisionLog(rows) });
      })
      .catch(() => {
        // No log read; the step reports no decisions rather than hiding the page.
      });
    return () => {
      cancelled = true;
    };
  }, [review, decisionScope]);

  useEffect(() => {
    if (analysisKey === null) return;
    let cancelled = false;
    void listExports().then((rows) => {
      if (!cancelled) setExportedRead({ key: analysisKey, exported: rows.some((row) => row.analysisKey === analysisKey) });
    });
    return () => {
      cancelled = true;
    };
  }, [analysisKey]);

  const decisions = useMemo(() => {
    if (review === null || profile === null) return null;
    return countDecisionProgress(classifyCells(review, profile), resolveDecisions(log));
  }, [review, profile, log]);

  return { decisions, exported };
}
